/**
 * MY PORTAL PAGE
 * Personal financial dashboard for partners
 * Apple Liquid Glass Design System - WWDC25
 */

import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  DollarSign,
  Calendar,
  CreditCard,
  Building2,
  Wallet,
  ChevronDown,
  ChevronUp,
  ArrowDownRight,
  ArrowUpRight,
  TrendingUp,
  Clock,
  Percent,
  User,
  Mail,
  Hash
} from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { apiClient } from '../../lib/api'
import { GlassCard, ContentCard } from '../../components/ui/GlassCard'
import { Badge, StatusBadge } from '../../components/ui/Badge'
import { StatCard, StatCardGrid } from '../../components/ui/StatCard'
import { Skeleton, SkeletonStatCard, SkeletonCard } from '../../components/ui/Skeleton'

// Animation configurations
const springConfig = {
  type: "spring" as const,
  damping: 25,
  stiffness: 300
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.1
    }
  }
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: springConfig
  }
}

const listItemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: springConfig
  }
}

interface PortalData {
  partner: {
    id: number
    name: string
    email: string
    share_percentage: number
    bank_name: string
    bank_account: string
    bank_iban: string
    status: string
    join_date: string
  }
  currentBalance: {
    usd: number
    pkr: number
    asOf: string | null
  }
  distributions: Array<{
    id: number
    year: number
    month: string
    opening_balance_usd: number
    opening_balance_pkr: number
    income_usd: number
    income_pkr: number
    ending_balance_usd: number
    ending_balance_pkr: number
  }>
  advances: {
    history: Array<{
      id: number
      amount: number
      currency: string
      status: string
      reason: string
      created_at: string
    }>
    totalTaken: {
      usd: number
      pkr: number
    }
  }
}

export function MyPortalPage() {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [portalData, setPortalData] = useState<PortalData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showDistributions, setShowDistributions] = useState(false)
  const [showAdvances, setShowAdvances] = useState(false)

  useEffect(() => {
    if (user) {
      fetchPortalData()
    }
  }, [user])

  const fetchPortalData = async () => {
    console.log('MyPortal: fetchPortalData called')
    try {
      const data = await apiClient.get('/financials/my-portal')
      console.log('MyPortal: API response:', data)

      if (data.success) {
        console.log('MyPortal: Setting portal data:', data.data)
        setPortalData(data.data)
      } else {
        console.log('MyPortal: API error:', data.error)
        setError(data.error || 'Failed to load portal data')
      }
    } catch (err: any) {
      console.error('MyPortal: Fetch error:', err)
      setError(err.message || 'Network error loading portal data')
    } finally {
      setLoading(false)
      console.log('MyPortal: Loading complete')
    }
  }

  const formatCurrency = (amount: number, currency: string) => {
    if (currency === 'PKR') {
      return new Intl.NumberFormat('en-PK', {
        style: 'currency',
        currency: 'PKR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount)
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount)
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const getMonthName = (monthStr: string) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
                    'July', 'August', 'September', 'October', 'November', 'December']
    const monthNum = parseInt(monthStr) - 1
    return months[monthNum] || monthStr
  }

  console.log('MyPortal render - loading:', loading, 'error:', error, 'hasData:', !!portalData)

  // Loading state
  if (loading) {
    console.log('MyPortal: Rendering loading state')
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="p-8 space-y-8"
      >
        {/* Header skeleton */}
        <div className="space-y-2">
          <Skeleton className="h-9 w-48" />
          <Skeleton className="h-5 w-72" />
        </div>

        {/* Profile card skeleton */}
        <SkeletonCard className="h-48" />

        {/* Stats grid skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <SkeletonStatCard />
          <SkeletonStatCard />
          <SkeletonStatCard />
          <SkeletonStatCard />
        </div>

        {/* Sections skeleton */}
        <SkeletonCard className="h-24" />
        <SkeletonCard className="h-24" />
      </motion.div>
    )
  }

  // Error state
  if (error || !portalData) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-8"
      >
        <ContentCard>
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red/10 flex items-center justify-center">
              <Wallet className="w-8 h-8 text-red" />
            </div>
            <h3 className="text-headline font-semibold text-light-text-primary dark:text-dark-text-primary mb-2">
              Unable to Load Portal
            </h3>
            <p className="text-subheadline text-light-text-secondary dark:text-dark-text-secondary">
              {error || 'Failed to load portal data'}
            </p>
          </div>
        </ContentCard>
      </motion.div>
    )
  }

  console.log('MyPortal: Rendering main content with data:', portalData)

  return (
    <div className="p-8 space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-title1 font-bold text-light-text-primary dark:text-dark-text-primary mb-2">
          My Portal
        </h1>
        <p className="text-body text-light-text-secondary dark:text-dark-text-secondary">
          Your personal financial overview
        </p>
      </div>

      {/* Partner Profile Card */}
      <motion.div>
        <GlassCard className="p-6">
          <div className="flex items-start gap-6">
            {/* Avatar */}
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple to-blue flex items-center justify-center shadow-glass dark:shadow-dark-glass flex-shrink-0"
            >
              <span className="text-3xl font-bold text-white">
                {portalData.partner.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
              </span>
            </motion.div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <h2 className="text-title2 font-bold text-light-text-primary dark:text-dark-text-primary">
                  {portalData.partner.name}
                </h2>
                <StatusBadge
                  status={portalData.partner.status === 'Active' ? 'success' : 'neutral'}
                >
                  {portalData.partner.status}
                </StatusBadge>
              </div>

              <div className="flex items-center gap-2 text-subheadline text-light-text-secondary dark:text-dark-text-secondary mb-3">
                <Mail className="w-4 h-4" />
                <span>{portalData.partner.email}</span>
              </div>

              <div className="flex items-center gap-4 flex-wrap">
                <Badge variant="purple" icon={<Percent className="w-3 h-3" />}>
                  {portalData.partner.share_percentage}% Share
                </Badge>
                {portalData.partner.join_date && (
                  <Badge variant="blue" icon={<Calendar className="w-3 h-3" />}>
                    Joined {formatDate(portalData.partner.join_date)}
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Bank Details */}
          {portalData.partner.bank_name && (
            <div className="mt-6 pt-6 border-t border-black/[0.06] dark:border-white/[0.06]">
              <div className="flex items-center gap-2 mb-4">
                <Building2 className="w-4 h-4 text-light-text-tertiary dark:text-dark-text-tertiary" />
                <span className="text-subheadline font-medium text-light-text-secondary dark:text-dark-text-secondary">
                  Bank Details
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="bg-white/30 dark:bg-white/5 rounded-xl p-4 border border-black/[0.04] dark:border-white/[0.04]"
                >
                  <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary mb-1">Bank Name</p>
                  <p className="text-body font-medium text-light-text-primary dark:text-dark-text-primary">
                    {portalData.partner.bank_name}
                  </p>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="bg-white/30 dark:bg-white/5 rounded-xl p-4 border border-black/[0.04] dark:border-white/[0.04]"
                >
                  <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary mb-1">Account Number</p>
                  <p className="text-body font-medium text-light-text-primary dark:text-dark-text-primary font-mono">
                    {portalData.partner.bank_account || '—'}
                  </p>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="bg-white/30 dark:bg-white/5 rounded-xl p-4 border border-black/[0.04] dark:border-white/[0.04]"
                >
                  <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary mb-1">IBAN</p>
                  <p className="text-body font-medium text-light-text-primary dark:text-dark-text-primary font-mono text-sm truncate">
                    {portalData.partner.bank_iban || '—'}
                  </p>
                </motion.div>
              </div>
            </div>
          )}
        </GlassCard>
      </motion.div>

      {/* Balance Cards */}
      <motion.div>
        <StatCardGrid columns={4}>
          <StatCard
            title="USD Balance"
            value={formatCurrency(portalData.currentBalance.usd, 'USD')}
            icon={DollarSign}
            color="blue"
            badge={portalData.currentBalance.asOf ? (
              <Badge variant="neutral" size="sm">As of {portalData.currentBalance.asOf}</Badge>
            ) : undefined}
          />
          <StatCard
            title="PKR Balance"
            value={formatCurrency(portalData.currentBalance.pkr, 'PKR')}
            icon={Wallet}
            color="green"
            badge={portalData.currentBalance.asOf ? (
              <Badge variant="neutral" size="sm">As of {portalData.currentBalance.asOf}</Badge>
            ) : undefined}
          />
          <StatCard
            title="Your Share"
            value={`${portalData.partner.share_percentage}%`}
            icon={Percent}
            color="purple"
            subtitle="Of monthly profits"
          />
          <StatCard
            title="Advances Taken"
            value={formatCurrency(portalData.advances.totalTaken.usd, 'USD')}
            icon={CreditCard}
            color="orange"
            subtitle={formatCurrency(portalData.advances.totalTaken.pkr, 'PKR')}
          />
        </StatCardGrid>
      </motion.div>

      {/* Distribution History */}
      <motion.div>
        <ContentCard>
          <motion.button
            onClick={() => setShowDistributions(!showDistributions)}
            className="w-full flex items-center justify-between p-2 -m-2 rounded-xl transition-colors hover:bg-black/[0.02] dark:hover:bg-white/[0.02]"
            whileTap={{ scale: 0.99 }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-green to-blue rounded-xl flex items-center justify-center shadow-sm">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <div className="text-left">
                <h2 className="text-headline font-semibold text-light-text-primary dark:text-dark-text-primary">
                  Distribution History
                </h2>
                <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary">
                  {portalData.distributions.length} records
                </p>
              </div>
            </div>
            <motion.div
              animate={{ rotate: showDistributions ? 180 : 0 }}
              transition={springConfig}
            >
              <ChevronDown className="w-5 h-5 text-light-text-tertiary dark:text-dark-text-tertiary" />
            </motion.div>
          </motion.button>

          <AnimatePresence>
            {showDistributions && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={springConfig}
                className="overflow-hidden"
              >
                <div className="pt-6 mt-6 border-t border-black/[0.06] dark:border-white/[0.06]">
                  {portalData.distributions.length === 0 ? (
                    <div className="text-center py-8">
                      <TrendingUp className="w-12 h-12 mx-auto mb-3 text-light-text-quaternary dark:text-dark-text-quaternary" />
                      <p className="text-subheadline text-light-text-secondary dark:text-dark-text-secondary">
                        No distribution history yet
                      </p>
                    </div>
                  ) : (
                    <motion.div
                      variants={containerVariants}
                      initial="hidden"
                      animate="visible"
                      className="space-y-3"
                    >
                      {portalData.distributions.map((dist, index) => (
                        <motion.div
                          key={dist.id || index}
                          variants={listItemVariants}
                          whileHover={{ scale: 1.01, x: 4 }}
                          className="p-4 bg-white/30 dark:bg-white/5 rounded-xl border border-black/[0.04] dark:border-white/[0.04]"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="text-body font-semibold text-light-text-primary dark:text-dark-text-primary">
                              {getMonthName(dist.month)} {dist.year}
                            </h3>
                            <Badge variant="blue" size="sm">
                              <Calendar className="w-3 h-3 mr-1" />
                              {dist.year}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                              <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary mb-1">USD Income</p>
                              <p className={`text-body font-medium ${dist.income_usd >= 0 ? 'text-green' : 'text-red'}`}>
                                {dist.income_usd >= 0 ? '+' : ''}{formatCurrency(dist.income_usd, 'USD')}
                              </p>
                            </div>
                            <div>
                              <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary mb-1">PKR Income</p>
                              <p className={`text-body font-medium ${dist.income_pkr >= 0 ? 'text-green' : 'text-red'}`}>
                                {dist.income_pkr >= 0 ? '+' : ''}{formatCurrency(dist.income_pkr, 'PKR')}
                              </p>
                            </div>
                            <div>
                              <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary mb-1">USD Balance</p>
                              <p className="text-body font-medium text-light-text-primary dark:text-dark-text-primary">
                                {formatCurrency(dist.ending_balance_usd, 'USD')}
                              </p>
                            </div>
                            <div>
                              <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary mb-1">PKR Balance</p>
                              <p className="text-body font-medium text-light-text-primary dark:text-dark-text-primary">
                                {formatCurrency(dist.ending_balance_pkr, 'PKR')}
                              </p>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </motion.div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </ContentCard>
      </motion.div>

      {/* Advance History */}
      <motion.div>
        <ContentCard>
          <motion.button
            onClick={() => setShowAdvances(!showAdvances)}
            className="w-full flex items-center justify-between p-2 -m-2 rounded-xl transition-colors hover:bg-black/[0.02] dark:hover:bg-white/[0.02]"
            whileTap={{ scale: 0.99 }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-orange to-red rounded-xl flex items-center justify-center shadow-sm">
                <CreditCard className="w-5 h-5 text-white" />
              </div>
              <div className="text-left">
                <h2 className="text-headline font-semibold text-light-text-primary dark:text-dark-text-primary">
                  Advance History
                </h2>
                <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary">
                  {portalData.advances.history.length} requests
                </p>
              </div>
            </div>
            <motion.div
              animate={{ rotate: showAdvances ? 180 : 0 }}
              transition={springConfig}
            >
              <ChevronDown className="w-5 h-5 text-light-text-tertiary dark:text-dark-text-tertiary" />
            </motion.div>
          </motion.button>

          <AnimatePresence>
            {showAdvances && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={springConfig}
                className="overflow-hidden"
              >
                <div className="pt-6 mt-6 border-t border-black/[0.06] dark:border-white/[0.06]">
                  {portalData.advances.history.length === 0 ? (
                    <div className="text-center py-8">
                      <CreditCard className="w-12 h-12 mx-auto mb-3 text-light-text-quaternary dark:text-dark-text-quaternary" />
                      <p className="text-subheadline text-light-text-secondary dark:text-dark-text-secondary">
                        No advance requests yet
                      </p>
                    </div>
                  ) : (
                    <motion.div
                      variants={containerVariants}
                      initial="hidden"
                      animate="visible"
                      className="space-y-3"
                    >
                      {portalData.advances.history.map((advance, index) => (
                        <motion.div
                          key={advance.id || index}
                          variants={listItemVariants}
                          whileHover={{ scale: 1.01, x: 4 }}
                          className="flex items-center justify-between p-4 bg-white/30 dark:bg-white/5 rounded-xl border border-black/[0.04] dark:border-white/[0.04]"
                        >
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                              advance.status === 'Approved'
                                ? 'bg-green/10 text-green'
                                : advance.status === 'Pending'
                                ? 'bg-orange/10 text-orange'
                                : 'bg-red/10 text-red'
                            }`}>
                              {advance.status === 'Approved' ? (
                                <ArrowDownRight className="w-5 h-5" />
                              ) : advance.status === 'Pending' ? (
                                <Clock className="w-5 h-5" />
                              ) : (
                                <ArrowUpRight className="w-5 h-5" />
                              )}
                            </div>
                            <div>
                              <p className="text-body font-semibold text-light-text-primary dark:text-dark-text-primary">
                                {formatCurrency(advance.amount, advance.currency)}
                              </p>
                              <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary">
                                {advance.reason || 'Advance request'}
                              </p>
                            </div>
                          </div>
                          <div className="text-right flex flex-col items-end gap-1">
                            <StatusBadge
                              status={
                                advance.status === 'Approved' ? 'success' :
                                advance.status === 'Pending' ? 'warning' : 'error'
                              }
                            >
                              {advance.status}
                            </StatusBadge>
                            <p className="text-caption2 text-light-text-tertiary dark:text-dark-text-tertiary">
                              {formatDate(advance.created_at)}
                            </p>
                          </div>
                        </motion.div>
                      ))}
                    </motion.div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </ContentCard>
      </motion.div>
    </div>
  )
}
